import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useGlobalContext } from "../../../Context/GlobalContext";
import styles from "../../RightSidebar/rightsidebar.module.css";
import SearchBar from "../../Search/SearchBar";
import { getServicesList,} from "../../../APICalling/APICalling";
import { highlightQuery, formatDateToDDMMYYYY } from "../../../Utils/utils";
import { getEffectivePriceFromRate } from "../../../Utils/priceRateHelpers";
import useClickOutside from "../../../Utils/Hooks/useClickOutside";
import useKeyboardNavigation from "../../../Utils/Hooks/useKeyboardNavigation";
import logger from "../../../Utils/logger";

const DEFAULT_BOOKING_GROUP_ID = "main";

const getBookingGroupIdFromCounterKey = (counterKey) => {
	const normalized = String(counterKey ?? "").trim();
	if (!normalized) return DEFAULT_BOOKING_GROUP_ID;
	const separatorIndex = normalized.indexOf("-");
	return separatorIndex === -1
		? DEFAULT_BOOKING_GROUP_ID
		: normalized.slice(0, separatorIndex) || DEFAULT_BOOKING_GROUP_ID;
};

const toAbsoluteMinutesFromDateAndTime = (dateStr, timeStr) => {
	const [day, month, year] = String(dateStr || "").split("-").map(Number);
	const [hour, minute] = String(timeStr || "").split(":").map(Number);
	if (
		[day, month, year, hour, minute].some((value) => Number.isNaN(value))
	) {
		return null;
	}
	return Date.UTC(year, month - 1, day, hour, minute) / 60000;
};

const getSlotComparableStart = (slot) => {
	if (Number.isFinite(slot?.absoluteStartMinutes)) {
		return slot.absoluteStartMinutes;
	}
	const serviceAt = String(slot?.service_at || "").trim();
	if (serviceAt) {
		const [datePart, timePart] = serviceAt.split(" ");
		const parsed = toAbsoluteMinutesFromDateAndTime(datePart, timePart);
		if (Number.isFinite(parsed)) {
			return parsed;
		}
	}
	const fallback = toAbsoluteMinutesFromDateAndTime(
		slot?.start_date,
		String(slot?.start_time || "").slice(0, 5)
	);
	return Number.isFinite(fallback) ? fallback : Number.POSITIVE_INFINITY;
};

const getSlotBookingGroupId = (slot) => {
	if (slot?.paxIndex != null && String(slot.paxIndex).trim() !== "") {
		return String(slot.paxIndex);
	}
	return getBookingGroupIdFromCounterKey(slot?.counterKey);
};

const getFirstServiceStartTimeByBookingGroup = (slots = []) => {
	const firstStartByGroup = new Map();
	const firstComparableByGroup = new Map();

	for (const slot of Array.isArray(slots) ? slots : []) {
		const groupId = getSlotBookingGroupId(slot);
		const startTime = String(slot?.start_time || "").slice(0, 5);
		if (!startTime) continue;

		const comparableStart = getSlotComparableStart(slot);
		const currentBest = firstComparableByGroup.get(groupId);

		if (currentBest == null || comparableStart < currentBest) {
			firstComparableByGroup.set(groupId, comparableStart);
			firstStartByGroup.set(groupId, startTime);
		}
	}

	return firstStartByGroup;
};

// Reusable service dropdown with keyboard navigation
const ServiceDropdown = ({ onSelect, onClose, selectedService, bookingGroupId }) => {
	const { 
		bookingData, 
		selectedOutlet, 
		selectedOutletData,
		selectedDate,
		selectedTime,
		isMembershipApplied,
		publicHoliday,
		newAppointmentSlots,
	} = useGlobalContext();

	const getServicePrice = (service, serviceTime) => {
		const n = getEffectivePriceFromRate(service?.price_rate, service, {
			isMembershipApplied,
			publicHoliday,
			selectedDate,
			serviceTime,
		});
		return Number.isFinite(n) ? n : "";
	};

	const firstServiceStartTimesByGroup = useMemo(
		() => getFirstServiceStartTimeByBookingGroup(newAppointmentSlots),
		[newAppointmentSlots]
	);

	const resolvedBookingGroupId = useMemo(
		() =>
			bookingGroupId ||
			getBookingGroupIdFromCounterKey(selectedService?.counterKey) ||
			DEFAULT_BOOKING_GROUP_ID,
		[bookingGroupId, selectedService?.counterKey]
	);

	const pricingServiceTime =
		firstServiceStartTimesByGroup.get(resolvedBookingGroupId) ||
		(resolvedBookingGroupId !== DEFAULT_BOOKING_GROUP_ID
			? firstServiceStartTimesByGroup.get(DEFAULT_BOOKING_GROUP_ID)
			: "") ||
		selectedTime?.value ||
		"";

	const [searchServiceQuery, setSearchServiceQuery] = useState("");
	const [serviceCategories, setServiceCategories] = useState([]);
	const [isLoadingServices, setIsLoadingServices] = useState(false);
	const selectedOutletID = selectedOutlet || bookingData?.outlet;	

	const dropdownRef = useRef();
	const searchInputRef = useRef();
	const selectedServiceRef = useRef(null);
	useClickOutside(dropdownRef, onClose);

    const fetchServiceList = useCallback(async () => {
		if (!selectedOutletID) {
			console.warn("No outlet selected, cannot fetch services.");
			return;
		}
	
		try {
			setIsLoadingServices(true);
			let item = {
				outlet: selectedOutletID,
				pagination: 0,
				status: 1,
				outlet_type: selectedOutletData?.outlet_type_id,
				service_at: `${formatDateToDDMMYYYY(selectedDate)} ${selectedTime?.value || ""}`,
			};
	
			// 🔑 Unique cache key based on filters
			const cacheKey = `serviceList_${JSON.stringify(item)}`;
			const etagKey = `${cacheKey}_etag`;
	
			const storedETag = sessionStorage.getItem(etagKey);
			const cachedData = sessionStorage.getItem(cacheKey);
	
			// ✅ Call API. Some callers return full axios response, others return response.data only.
			const response = await getServicesList(item, storedETag);
			const responseStatus =
				typeof response?.status === "number"
					? response.status
					: response?.data?.success || response?.success
					? 200
					: undefined;
			const payload = response?.data?.data ? response.data : response;

			// 🔥 CASE 1: 304 → use cache
			if (responseStatus === 304) {
				if (cachedData) {
					setServiceCategories(JSON.parse(cachedData));
				} else {
					console.warn("304 received but no cache found");
					setServiceCategories([]);
				}
				return;
			}
	
			// 🔥 CASE 2: 200/success payload → update cache (+ ETag when available)
			if (responseStatus === 200) {
				const serviceList =
					payload?.data?.list?.category ||
					payload?.data?.data?.list?.category ||
					[];
	
				// Save data
				sessionStorage.setItem(cacheKey, JSON.stringify(serviceList));
	
				// Save ETag
				const newETag = response?.headers?.etag || response?.headers?.ETag;
				if (newETag) {
					sessionStorage.setItem(etagKey, newETag);
				}
	
				setServiceCategories(serviceList);
				return;
			}

			setServiceCategories([]);
		} catch (error) {
			console.error("Error fetching services:", error);
			setServiceCategories([]);
		} finally {
			setIsLoadingServices(false);
		}
	}, [selectedOutletID, selectedOutletData?.outlet_type, selectedDate, selectedTime]);


	useEffect(() => {
		// Skip API call if serviceCategories already has data
		if (serviceCategories.length > 0) {
			return;
		}
		
		if (selectedOutletID && bookingData?.time) {
			fetchServiceList();
		}
	}, [selectedOutletID, bookingData?.time, serviceCategories.length, fetchServiceList]);

	// Helper function to check if a service matches the selected service
	const isServiceMatch = useCallback((item) => {
		if (!selectedService) return false;
		
		// Match by id or service property
		const selectedId = selectedService.id || selectedService.service;
		const itemId = item.id;
		
		if (selectedId && itemId && Number(selectedId) === Number(itemId)) {
			return true;
		}
		
		// Fallback: match by name and duration
		const selectedDuration = selectedService.groupBaseDuration ?? selectedService.duration;
		if (selectedService.name === item.name && Number(selectedDuration) === Number(item.duration)) {
			return true;
		}
		
		return false;
	}, [selectedService]);

	useEffect(() => {
		if (dropdownRef.current) {
			// Scroll to show dropdown inside nearest scrollable container (like .divHeight)
			dropdownRef.current.scrollIntoView({
				// behavior: "smooth",
				block: "nearest",
			});
		}
		// Focus search input when dropdown opens
		if (searchInputRef.current) {
			searchInputRef.current.focus();
			searchInputRef.current.select();
		}
	}, []);

	// Scroll to selected service when services are loaded or selected service changes
	useEffect(() => {
		if (selectedService && serviceCategories.length > 0 && selectedServiceRef.current) {
			// Use setTimeout to ensure DOM is fully rendered
			const timeoutId = setTimeout(() => {
				selectedServiceRef.current?.scrollIntoView({
					// behavior: "smooth",
					block: "center",
				});
			}, 150);
			
			return () => clearTimeout(timeoutId);
		}
	}, [selectedService, serviceCategories]);

	const attachServiceCategory = (item, category) => ({
		...item,
		service_category: category?.name || category?.category_name || item?.service_category || "",
		service_category_name: category?.name || category?.category_name || item?.service_category_name || "",
		service_category_id: category?.id ?? category?.category_id ?? item?.service_category_id ?? null,
	});

	// Flatten and filter services for keyboard navigation
	const visibleServices = useMemo(() => {
		if (!serviceCategories.length) return [];
		return serviceCategories.flatMap(category => 
			(category.services?.filter(item => 
				`${item.duration} mins ${item.name} $${item.price}`
				.toLowerCase()
				.includes(searchServiceQuery.toLowerCase())
			) || []).map((item) => attachServiceCategory(item, category))
		);
	}, [serviceCategories, searchServiceQuery]);

	// Flat ref for the scrollable containerList, though we can use the dropdownRef if the list is within it.
	// Actually, the styles.serviceList is the scrollable container.
	const listRef = useRef(null);

	const { 
		selectedIndex, 
		setSelectedIndex, 
		handleKeyDown: handleKeyboardNav,
		setIsKeyboardNavActive,
		isKeyboardNavActive
	} = useKeyboardNavigation(
		visibleServices.length,
		onSelect,
		{ listRef }
	);

	const handleKeyDown = (e) => {
		handleKeyboardNav(e, (index) => visibleServices[index]);
	};

	return (
		<div className={`${styles.dropShow} ${styles.rightbarDropdown}`} ref={dropdownRef} >
			<SearchBar
				ref={searchInputRef}
				placeholder="Search Services"
				value={searchServiceQuery}
				onChange={(e) => setSearchServiceQuery(e.target.value)}
				onKeyDown={handleKeyDown}
				className={styles.sidebarSearch}
				iconClassName={styles.rightbarSearch}
			/>
			<div className={styles.serviceList} ref={listRef}>
				{isLoadingServices ? (
					<p className={styles.dropdownLoader}>Loading...</p>
				) : serviceCategories.length > 0 ? (
					(() => {
						let globalIndex = 0; // To track index across categories
						return serviceCategories.map((service, index) => {
							const filteredItems = service.services?.filter((item) =>
								`${item.duration} mins ${item.name} $${item.price}`
									.toLowerCase()
									.includes(searchServiceQuery.toLowerCase())
							);

							if (!filteredItems?.length) return null;
							
							return (
								<div className={styles.serviceMain} key={index}>
									<h4>{service.name}</h4>
									<ul>
										{filteredItems.map((item, idx) => {
											const currentGlobalIndex = globalIndex++;
											const isSelected = selectedIndex === currentGlobalIndex;
											const isCurrentService = isServiceMatch(item);
											
											return (
												<li
													key={idx}
													ref={isCurrentService ? selectedServiceRef : null}
													data-key-nav-index={currentGlobalIndex}
													onClick={() => {onSelect(attachServiceCategory(item, service))}}
													onMouseEnter={() => {
														if (!isKeyboardNavActive) {
															setSelectedIndex(currentGlobalIndex);
														}
													}}
													onMouseMove={() => {
														if (isKeyboardNavActive) {
															setIsKeyboardNavActive(false);
															setSelectedIndex(currentGlobalIndex);
														}
													}}
													className={`${styles.serviceListItem} ${isSelected ? styles.selectedServiceItem : ''} ${isCurrentService ? styles.currentSelectedService : ''}`}>
													<span className={styles.serviceName}>
														{highlightQuery(`${item.duration} Mins ${item.name}`,searchServiceQuery)}
													</span>
													<span className={styles.servicePrice}>
														{highlightQuery(`
															${getServicePrice(item, pricingServiceTime)}
															`,searchServiceQuery)}
													</span>
												</li>
											);
										})}
									</ul>
								</div>
							);
						});
					})()
				) : (
					<p className={styles.notFoundCustomer}>No Services Found</p>
				)}
			</div>
		</div>
	);
};
export default ServiceDropdown;